import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction
} from 'mobx';

import { LoadingStage } from 'shared/entities/meta';
import { apiUrls } from 'shared/entities/domain';
import ListModel from 'shared/models/ListModel';
import {
  UserCommonVarModel,
  UserAttachmentVarModel,
  UserDateVarModel
} from 'shared/models/vars';
import { IUserItem, UserItemServer } from 'shared/entities/usersList';
import { normalizeUserNote } from 'shared/entities/userNote';
import { IRootStore } from 'shared/entities/store/rootStore';
import { ChannelKindType } from 'shared/entities/channels';
import { VarType } from 'shared/entities/vars';
import { FileBaseServer } from 'shared/entities/file';

export default class UserItemModel implements IUserItem {
  readonly id: string;
  readonly userName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly chatId: string | null;
  readonly channelId: string;
  readonly externalId: string;
  readonly url: string | null;
  readonly channelKind: ChannelKindType;
  readonly vars: ListModel<
    UserCommonVarModel | UserAttachmentVarModel | UserDateVarModel
  > = new ListModel<
    UserCommonVarModel | UserAttachmentVarModel | UserDateVarModel
  >();
  readonly phone: string | null;
  readonly email: string | null;
  readonly photoUrl: string | null;
  notes: IUserItem['notes'] = [];
  unreadMentionNoteIds: Set<string> = new Set();

  selected = false;

  removingFromListStage: LoadingStage = LoadingStage.NOT_STARTED;
  removingStage: LoadingStage = LoadingStage.NOT_STARTED;

  readonly rootStore: IRootStore;

  constructor({
    userName,
    firstName,
    lastName,
    id,
    rootStore,
    externalId,
    channelId,
    url,
    channelKind,
    phone,
    email,
    photoUrl,
    notes = [],
    unreadMentionNoteIds = new Set<string>()
  }: Omit<IUserItem, 'vars'> & { rootStore: IRootStore }) {
    this.userName = userName;
    this.firstName = firstName;
    this.lastName = lastName;
    this.id = id;
    this.rootStore = rootStore;
    this.externalId = externalId;
    this.channelId = channelId;
    this.url = url;
    this.channelKind = channelKind;
    this.phone = phone;
    this.email = email;
    this.photoUrl = photoUrl;
    this.notes = notes;
    this.unreadMentionNoteIds = unreadMentionNoteIds;

    makeObservable(this, {
      selected: observable,
      removingFromListStage: observable,
      removingStage: observable,
      vars: observable,
      notes: observable,
      unreadMentionNoteIds: observable,

      hasUnreadMentions: computed,
      uniqueKey: computed,
      fullName: computed,

      setSelected: action.bound,
      removeUnreadMentionNoteIds: action,
      addUnreadMentionNoteId: action,
      remove: action
    });
  }

  get hasUnreadMentions(): boolean {
    return this.unreadMentionNoteIds.size > 0;
  }

  removeUnreadMentionNoteIds = (noteIds: string[]): void => {
    noteIds.forEach((noteId) => this.unreadMentionNoteIds.delete(noteId));
  };

  addUnreadMentionNoteId = (noteId: string): void => {
    this.unreadMentionNoteIds.add(noteId);
  };

  get fullName(): string {
    const fullName = `${this.firstName}${
      this.lastName ? ` ${this.lastName}` : ''
    }`;

    if (fullName.length) {
      return fullName;
    }

    return this.userName || '';
  }

  get uniqueKey(): string {
    return `${this.channelId}_${this.externalId}`;
  }

  setSelected(value: boolean): void {
    this.selected = value;
  }

  async remove(): Promise<BaseResponse> {
    if (this.removingStage === LoadingStage.LOADING) {
      return {
        isError: true
      };
    }

    this.removingStage = LoadingStage.LOADING;

    const { isError } = await this.rootStore.networkStore.api(
      apiUrls.USERS_DELETE,
      {
        method: 'POST',
        data: {
          user_ids: [this.id]
        }
      }
    );

    runInAction(() => {
      if (!isError) {
        this.removingStage = LoadingStage.SUCCESS;
      } else {
        this.removingStage = LoadingStage.ERROR;
      }
    });

    return {
      isError
    };
  }

  async removeFromList({ listId }: { listId: string }): Promise<LoadingStage> {
    if (this.removingFromListStage === LoadingStage.LOADING) {
      return LoadingStage.ERROR;
    }

    this.removingFromListStage = LoadingStage.LOADING;

    const { isError } = await this.rootStore.networkStore.api(
      apiUrls.USERS_REMOVE_USERS_FROM_LISTS,
      {
        method: 'POST',
        data: {
          user_ids: [this.id],
          list_ids: [listId]
        }
      }
    );

    runInAction(() => {
      if (!isError) {
        this.removingFromListStage = LoadingStage.SUCCESS;
      } else {
        this.removingFromListStage = LoadingStage.ERROR;
      }
    });

    return this.removingFromListStage;
  }

  static fromJson(
    raw: UserItemServer & {
      rootStore: IRootStore;
      chatId: string | null;
      fallbackPhotoUrl?: string | null;
      /** Только из users/get: в UserItemServer этого поля нет, см. UserCardServer */
      unreadMentionNoteIds?: string[];
    }
  ): UserItemModel {
    const user = new UserItemModel({
      id: raw._id,
      userName: raw.username,
      firstName: raw.first_name,
      lastName: raw.last_name,
      rootStore: raw.rootStore,
      externalId: raw.external_id,
      channelId: raw.channel_id,
      url: raw.url,
      channelKind: raw.channel_kind,
      phone: raw.phone ?? null,
      email: raw.email ?? null,
      notes: raw.notes?.map(normalizeUserNote) ?? [],
      photoUrl: raw.photo?.url?.trim() || raw.fallbackPhotoUrl || null,
      unreadMentionNoteIds: new Set(raw.unreadMentionNoteIds ?? [])
    });

    if (!raw.vars || !raw.chatId) {
      return user;
    }

    const { keys, entities } = Object.keys(raw.vars).reduce(
      (acc, varKey) => {
        if (!raw.vars || !raw.chatId) {
          return acc;
        }
        const varValue = raw.vars[varKey];
        const variableFromStore =
          raw.rootStore.variablesStore.getVariableByKey(varKey);

        if (!variableFromStore) {
          return acc;
        }

        let varItem;
        switch (variableFromStore.type) {
          case VarType.date:
            varItem = UserDateVarModel.fromJson({
              value: varValue as string | null,
              key: varKey,
              chatId: raw.chatId,
              user,
              rootStore: raw.rootStore,
              type: VarType.date
            });
            break;
          case VarType.datetime:
            varItem = UserDateVarModel.fromJson({
              value: varValue as string | null,
              key: varKey,
              chatId: raw.chatId,
              user,
              rootStore: raw.rootStore,
              type: VarType.datetime
            });
            break;
          case VarType.attachment:
            varItem = UserAttachmentVarModel.fromJson({
              key: varKey,
              value: varValue as FileBaseServer | null
            });
            break;
          default:
            varItem = UserCommonVarModel.fromJson({
              value: (varValue as string | null) ?? '',
              key: varKey,
              chatId: raw.chatId,
              user,
              rootStore: raw.rootStore
            });
        }

        return {
          ...acc,
          keys: [...acc.keys, varKey],
          entities: {
            ...acc.entities,
            [varKey]: varItem
          }
        };
      },
      { keys: [], entities: {} }
    );

    user.vars.addEntities({ entities, keys, initial: true });

    return user;
  }
}
